/**
 * The Countdown app's SHARED engine (feature 0041) — wire parsing, time math, palettes and the tick hook,
 * used by BOTH surfaces: the product-page banner (`main-product/Countdown.tsx`) and the site-wide bar
 * (`_shared/CountdownBar.tsx`). One arithmetic, one storage key convention, one set of presets: two copies
 * would drift the day a preset changes, and the visitor would see two different clocks on one page.
 *
 * The properties that make the countdown honest, all enforced here:
 *  - the FIRST render is computed from the SERVER props only (`serverNowEpochMs`, never `Date.now()`), so
 *    SSR and the hydration pass agree byte-for-byte;
 *  - DATE / DAILY tick on a server-anchored offset, immune to a visitor's wrong wall clock;
 *  - EVERGREEN's deadline belongs to the VISITOR (localStorage `aqx-cd-<campaignId>`) and SURVIVES reloads;
 *  - parsing is DEFENSIVE — the payload crosses a JSON boundary and an SSR throw blanks a public page.
 */
import { t } from '@aquilixshop/i18n';
import { useEffect, useRef, useState } from 'react';
import { anchorsFor, initialRemainingSeconds, tickOnce, } from './countdownClock.js';
/* ---------------------------------------------------------------- parsing */
const TIMER_TYPES = ['DATE', 'EVERGREEN', 'DAILY'];
const EXPIRIES = ['HIDE', 'ZERO', 'RESTART'];
// `button` / `buttonText` (24/09/2026): the CTA used to borrow the digit colour and the strip colour, so a
// seller could not colour the button without recolouring the clock. Absent = the old borrowing.
const COLOR_KEYS = [
    'background',
    'text',
    'digit',
    'digitBackground',
    'button',
    'buttonText',
];
/** The weights a seller can pick; anything else on the wire is ignored, never rounded. */
const TEXT_WEIGHTS = [400, 500, 600, 700, 800];
export const TEXT_SIZE_MIN = 10;
export const TEXT_SIZE_MAX = 40;
/**
 * The DIGITS have their own size (04/10/2026). `textSize` only ever moved the title — the founder set it
 * to 24 and the clock stayed at 18 — so the one thing the surface exists to show was the one thing she
 * could not resize. The range runs higher than the title's: a clock is the headline of an urgency strip,
 * and 40px is small for one.
 */
export const DIGIT_SIZE_MIN = 10;
export const DIGIT_SIZE_MAX = 72;
const HEX_COLOR = /^#[0-9a-fA-F]{3}(?:[0-9a-fA-F]{3})?$/;
/**
 * Defensive wire → {@link ProductCountdown} parse (mirrors `parseCodConfig`'s discipline): the backend is
 * trusted but the payload crosses a JSON boundary and an SSR throw blanks a PUBLIC page, so anything
 * malformed returns `undefined` — the countdown simply does not render.
 */
export function parseProductCountdown(raw) {
    if (raw === null || typeof raw !== 'object')
        return undefined;
    const r = raw;
    const campaignId = typeof r.campaignId === 'string' && r.campaignId.trim() !== '' ? r.campaignId : undefined;
    const timerType = TIMER_TYPES.find((t) => t === r.timerType);
    const serverNow = typeof r.serverNowEpochMs === 'number' && Number.isFinite(r.serverNowEpochMs);
    if (!campaignId || !timerType || !serverNow)
        return undefined;
    const endsAtEpochMs = typeof r.endsAtEpochMs === 'number' && Number.isFinite(r.endsAtEpochMs)
        ? r.endsAtEpochMs
        : undefined;
    const minutes = typeof r.minutes === 'number' && Number.isFinite(r.minutes) && r.minutes > 0
        ? r.minutes
        : undefined;
    if (timerType === 'EVERGREEN' ? minutes === undefined : endsAtEpochMs === undefined)
        return undefined;
    const design = (typeof r.design === 'object' && r.design !== null ? r.design : {});
    const rawColors = (typeof design.colors === 'object' && design.colors !== null ? design.colors : {});
    const colors = {};
    for (const key of COLOR_KEYS) {
        const value = rawColors[key];
        if (typeof value === 'string' && HEX_COLOR.test(value))
            colors[key] = value;
    }
    const textSize = typeof design.textSize === 'number' &&
        Number.isInteger(design.textSize) &&
        design.textSize >= TEXT_SIZE_MIN &&
        design.textSize <= TEXT_SIZE_MAX
        ? design.textSize
        : undefined;
    const textWeight = TEXT_WEIGHTS.find((w) => w === design.textWeight);
    const digitSize = typeof design.digitSize === 'number' &&
        Number.isInteger(design.digitSize) &&
        design.digitSize >= DIGIT_SIZE_MIN &&
        design.digitSize <= DIGIT_SIZE_MAX
        ? design.digitSize
        : undefined;
    const visitorsRaw = (typeof r.visitors === 'object' && r.visitors !== null ? r.visitors : undefined);
    const visitors = visitorsRaw &&
        typeof visitorsRaw.message === 'string' &&
        typeof visitorsRaw.count === 'number' &&
        Number.isFinite(visitorsRaw.count) &&
        visitorsRaw.count > 0
        ? { message: visitorsRaw.message, count: Math.floor(visitorsRaw.count) }
        : undefined;
    return {
        campaignId,
        title: typeof r.title === 'string' ? r.title : undefined,
        subtitle: typeof r.subtitle === 'string' ? r.subtitle : undefined,
        timerType,
        endsAtEpochMs,
        serverNowEpochMs: r.serverNowEpochMs,
        minutes,
        onExpiry: EXPIRIES.find((e) => e === r.onExpiry) ?? 'HIDE',
        design: {
            preset: typeof design.preset === 'string' && design.preset !== '' ? design.preset : 'circles',
            colors,
            ...(textSize !== undefined ? { textSize } : {}),
            ...(textWeight !== undefined ? { textWeight } : {}),
            ...(digitSize !== undefined ? { digitSize } : {}),
        },
        visitors,
    };
}
/**
 * The title/subtitle metrics one surface paints: the seller's size and weight when set, else the surface's
 * own defaults. Each surface passes ITS defaults (a strip is 13px, a bubble 14px, the banner 17px), so a
 * campaign without typography settings keeps looking exactly as before — and a size the seller did set
 * scales the subtitle along, at the ratio that surface always had.
 */
export function textScale(design, defaults) {
    const size = design.textSize ?? defaults.size;
    const weight = design.textWeight ?? defaults.weight;
    const subtitle = design.textSize === undefined
        ? defaults.subtitle
        : Math.max(9, Math.round((design.textSize * defaults.subtitle) / defaults.size));
    return { size, weight, subtitle };
}
/**
 * The CLOCK's metrics on one surface: the seller's digit size when set, else the surface's own.
 *
 * The ratio matters as much as the size. A 44px round tile with 32px digits inside it is a digit that
 * has outgrown its plate — so each surface passes the digit size it has always painted, and multiplies
 * ITS furniture by the ratio. The clock then grows as one object instead of bursting out of itself.
 */
export function digitScale(design, defaultSize) {
    const size = design.digitSize ?? defaultSize;
    return { size, scale: size / defaultSize };
}
/**
 * THE PLATE UNDER A DIGIT, ON A FLOATING SURFACE (04/10/2026).
 *
 * The strip and the bubble never painted `digitBackground` at all — they have no tiles, only bare digits —
 * so one of the six colour slots the design panel offers was dead on the two surfaces that panel edits.
 *
 * It paints ONLY when the seller CHOSE a colour, never from the preset: `circles` carries a violet
 * `digitBackground` of its own, and honouring it here would hand every existing strip a row of violet
 * plates nobody asked for. An override is a decision; a preset value is a default for the surface that
 * has tiles. Returns `undefined` when there is nothing to paint.
 */
export function floatingDigitTile(design, scale) {
    const chosen = design.colors?.digitBackground;
    if (!chosen || chosen === 'transparent')
        return undefined;
    return {
        background: chosen,
        padding: `${scaled(3, scale)}px ${scaled(7, scale)}px`,
        borderRadius: scaled(6, scale),
    };
}
/** A furniture measurement scaled with the digits, never below `min` (a 1px gap is not a gap). */
export function scaled(value, scale, min = 1) {
    return Math.max(min, Math.round(value * scale));
}
/* ---------------------------------------------------------------- time math */
// The clock itself is framework-free and SHARED with the storefront's vanilla badge ticker — see
// `countdownClock`. Re-exported here so React surfaces keep one import site.
export { evergreenDeadline, initialRemainingSeconds, pad2, splitUnits, } from './countdownClock.js';
export const CIRCLES = {
    background: 'transparent',
    text: '#151515',
    digit: '#ffffff',
    digitBackground: '#533afd',
    dark: false,
    shape: 'circles',
};
export const PRESETS = {
    circles: CIRCLES,
    plain: {
        background: 'transparent',
        text: '#151515',
        digit: '#151515',
        digitBackground: 'transparent',
        dark: false,
        shape: 'flat',
    },
    boxed: {
        background: 'transparent',
        text: '#151515',
        digit: '#151515',
        digitBackground: '#f2f2f2',
        dark: false,
        shape: 'boxed',
    },
    dark: {
        background: '#111111',
        text: '#ffffff',
        digit: '#ffd60a',
        digitBackground: 'transparent',
        dark: true,
        shape: 'flat',
    },
    neon: {
        background: '#eaff00',
        text: '#111111',
        digit: '#111111',
        digitBackground: 'transparent',
        dark: false,
        shape: 'flat',
    },
    brand: {
        background: '#533afd',
        text: '#ffffff',
        digit: '#ffffff',
        digitBackground: 'transparent',
        dark: true,
        shape: 'flat',
    },
    night: {
        background: '#0b1e3f',
        text: '#ffffff',
        digit: '#ffd60a',
        digitBackground: 'transparent',
        dark: true,
        shape: 'flat',
    },
    mint: {
        background: '#b9f0d5',
        text: '#111111',
        digit: '#111111',
        digitBackground: 'transparent',
        dark: false,
        shape: 'flat',
    },
};
/** Preset base + the seller's hex overrides — the effective colours of one campaign. */
export function paletteFor(cd) {
    const base = PRESETS[cd.design.preset] ?? CIRCLES;
    const c = cd.design.colors ?? {};
    const background = c.background ?? base.background;
    const digit = c.digit ?? base.digit;
    return {
        ...base,
        background,
        text: c.text ?? base.text,
        digit,
        digitBackground: c.digitBackground ?? base.digitBackground,
        // The CTA's historical look: digits' colour on a strip-coloured label. A transparent surface has no
        // colour to lend, so the label reads white — the same substitution the floating repair makes.
        button: c.button ?? digit,
        buttonText: c.buttonText ?? (background === 'transparent' ? '#ffffff' : background),
    };
}
/**
 * The palette for a FLOATING surface — the bar and the popup, which sit over the seller's page rather than
 * inside it. A `transparent` preset means "no surface of my own": the banner can honour that (it renders in
 * the buy column, on the page's own background), a strip and a bubble cannot, so they substitute ink. That
 * substitution turns the light presets against themselves — `plain`'s near-black digits on a black strip are
 * invisible — so the preset's own colours are repaired here, ONCE, for both surfaces.
 *
 * A colour the seller typed is never repaired: they picked it, on that surface, on purpose.
 */
export function floatingPaletteFor(cd) {
    const merged = paletteFor(cd);
    if (merged.background !== 'transparent')
        return merged;
    const overrides = cd.design.colors ?? {};
    const base = PRESETS[cd.design.preset] ?? CIRCLES;
    // A boxed preset paints its digits on a light tile, which stays readable over ink; the others don't.
    const readableDigit = base.shape === 'boxed' ? base.digit : '#ffffff';
    const digit = overrides.digit ?? readableDigit;
    return {
        ...merged,
        background: '#111111',
        text: overrides.text ?? '#ffffff',
        digit,
        button: overrides.button ?? digit,
        buttonText: overrides.buttonText ?? '#111111',
        dark: true,
    };
}
/* ---------------------------------------------------------------- labels */
/**
 * The four unit labels, read from the dictionary rather than from a table of three languages.
 *
 * The table was correct and it worked — it was simply frozen at fr/en/ar, so the 27th language would
 * have shown a countdown in French next to Arabic body copy. One sentence, one home (feature 0050).
 */
export function unitLabels(locale) {
    const ctx = { locale };
    return [
        t(ctx, 'countdown.days'),
        t(ctx, 'countdown.hours'),
        t(ctx, 'countdown.minutes'),
        t(ctx, 'countdown.seconds'),
    ];
}
/* ---------------------------------------------------------------- the tick */
/**
 * The shared React clock. Returns the remaining seconds to render and whether the surface must
 * disappear (`HIDE` at zero). The first value is the SERVER-derived one — the effect only starts
 * ticking AFTER hydration, which is what keeps the SSR and hydrated trees identical.
 */
export function useCountdownTick(cd) {
    const [remaining, setRemaining] = useState(() => initialRemainingSeconds(cd));
    const [hidden, setHidden] = useState(false);
    // The tick's mutable anchors — established once, in the mount effect (never during render).
    const anchors = useRef({ offset: 0, deadline: 0 });
    useEffect(() => {
        anchors.current = anchorsFor(cd, Date.now());
        const run = () => {
            const result = tickOnce(cd, anchors.current);
            if (result.hidden)
                setHidden(true);
            else
                setRemaining(result.remaining);
        };
        run();
        const id = window.setInterval(run, 1000);
        return () => window.clearInterval(id);
        // The payload is immutable for the life of the page — re-anchoring on identity change would reset the tick.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cd.campaignId]);
    return { remaining, hidden };
}
//# sourceMappingURL=countdownEngine.js.map